import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { formatRand } from '../lib/productPlans';
import { startProductCheckout } from '../lib/payments';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

export type CartDrawerItem = {
  id: string;
  label: string;
  price: number;
  originalPrice?: number;
  discountAmount?: number;
  discountLabel?: string;
};

export type CartDrawerState = {
  planId: 'launch' | 'growth' | 'command';
  planName: string;
  billingMode: 'monthly' | 'buyout';
  basePrice: number;
  addOns: CartDrawerItem[];
  total: number;
  spec?: {
    summary: string;
    messages: Array<{ role: 'assistant' | 'client'; content: string }>;
    domain?: {
      needsRegistration: boolean;
      desiredDomain: string;
      availabilityStatus?: string;
      availabilityMode?: string;
      availabilityMessage?: string;
      isAvailable?: boolean | null;
      isPremium?: boolean | null;
      premiumReason?: string;
      purchaseAmount?: number;
    } | null;
    pricing?: {
      basePrice: number;
      addOnOriginalTotal: number;
      addOnDiscountTotal: number;
      addOnTotal: number;
      domainRegistrationTotal?: number;
      total: number;
      items: Array<{
        id: string;
        label: string;
        originalPrice: number;
        adjustedPrice: number;
        discountAmount: number;
        discountLabel?: string;
      }>;
    };
  };
};

type CartDrawerProps = {
  cart: CartDrawerState | null;
  isOpen: boolean;
  onClose: () => void;
};

const CartDrawer = ({ cart, isOpen, onClose }: CartDrawerProps) => {
  const [isCheckingOut, setIsCheckingOut] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const domainRegistrationTotal = cart?.spec?.pricing?.domainRegistrationTotal || 0;
  const domainCheckoutBlockReason = cart?.spec?.domain?.needsRegistration && cart.spec.domain.availabilityStatus !== 'available'
    ? cart.spec.domain.desiredDomain
      ? 'Please choose an available domain before checkout. We will not take payment for a domain we could not verify.'
      : 'Please enter and verify the desired domain before checkout.'
    : '';

  const updateCart = (nextCart: CartDrawerState | null) => {
    window.dispatchEvent(new CustomEvent('ddd:update-cart', { detail: nextCart }));
  };

  const removeAddOn = (id: string) => {
    if (!cart) return;

    const addOns = cart.addOns.filter((addon) => addon.id !== id);
    updateCart({
      ...cart,
      addOns,
      total: cart.basePrice + addOns.reduce((total, addon) => total + addon.price, 0) + domainRegistrationTotal,
      spec: cart.spec
        ? {
            ...cart.spec,
            pricing: cart.spec.pricing
              ? {
                  ...cart.spec.pricing,
                  addOnTotal: addOns.reduce((total, addon) => total + addon.price, 0),
                  total: cart.basePrice + addOns.reduce((total, addon) => total + addon.price, 0) + domainRegistrationTotal,
                }
              : cart.spec.pricing,
          }
        : cart.spec,
    });
  };

  const removeWebsite = () => {
    setMessage('');
    updateCart(null);
  };

  const handleCheckout = async () => {
    if (!cart) return;

    setMessage('');

    if (domainCheckoutBlockReason) {
      setMessage(domainCheckoutBlockReason);
      return;
    }

    if (!isSupabaseConfigured) {
      setMessage('Client accounts are not configured yet.');
      return;
    }

    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      window.dispatchEvent(new CustomEvent('ddd:open-auth', { detail: { mode: 'signup' } }));
      return;
    }

    setIsCheckingOut(true);
    try {
      const result = await startProductCheckout(
        cart.planId,
        cart.billingMode,
        cart.addOns.map((addon) => ({ id: addon.id })),
        cart.total,
        cart.spec,
      );

      if (result.mode === 'manual') {
        setMessage(result.message || 'Order saved. We will confirm payment manually.');
      }
    } catch (error: any) {
      setMessage(error.message || 'Checkout could not be started.');
    } finally {
      setIsCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[260] bg-black/55 backdrop-blur-sm"
          onMouseDown={onClose}
        >
          <motion.aside
            initial={{ x: '100%', filter: 'blur(10px)' }}
            animate={{ x: 0, filter: 'blur(0px)' }}
            exit={{ x: '100%', filter: 'blur(8px)' }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col overflow-hidden border-l border-orange-300/25 bg-[#060505] text-orange-50 shadow-[0_0_80px_rgba(249,115,22,0.22)]"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="pointer-events-none absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-orange-200/70 to-transparent" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,rgba(249,115,22,0.22),transparent_36%)]" />
            <div className="relative z-10 flex h-full flex-col p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.24em] text-orange-300">DesignDotDev cart</p>
                  <h2 className="mt-2 text-4xl font-black tracking-tight">Checkout</h2>
                  <p className="mt-3 text-sm leading-6 text-orange-100/58">
                    Your selected package and add-ons stay here while you move through the site.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-black uppercase tracking-wider text-orange-100 transition-colors hover:border-orange-300/60"
                >
                  Close
                </button>
              </div>

              <div className="mt-7 min-h-0 flex-1 overflow-y-auto pr-1">
                {!cart ? (
                  <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.035] p-5">
                    <p className="text-sm leading-6 text-orange-100/60">
                      No package selected yet. Open Packages, choose a tier, and the cart will build from there.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-[1.5rem] border border-orange-300/25 bg-orange-500/[0.07] p-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-300/75">Website product</p>
                        <button
                          type="button"
                          onClick={removeWebsite}
                          className="rounded-full border border-orange-300/30 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-orange-200 transition-colors hover:bg-orange-400 hover:text-black"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="mt-2 grid grid-cols-[1fr_auto] gap-4">
                        <div>
                          <p className="font-black text-orange-100">{cart.planName}</p>
                          <p className="mt-1 text-xs uppercase tracking-[0.16em] text-orange-100/42">
                            {cart.billingMode === 'monthly' ? 'Monthly plan' : 'Once-off buyout'}
                          </p>
                        </div>
                        <p className="font-black text-orange-200">
                          {formatRand(cart.basePrice)}
                          {cart.billingMode === 'monthly' ? '/m' : ''}
                        </p>
                      </div>
                      {cart.spec?.summary ? (
                        <p className="mt-3 rounded-2xl border border-orange-300/15 bg-black/25 p-3 text-xs leading-5 text-orange-100/55">
                          {cart.spec.summary}
                        </p>
                      ) : null}
                      {cart.spec?.domain ? (
                        <div className="mt-3 rounded-2xl border border-orange-300/15 bg-black/25 p-3 text-xs leading-5 text-orange-100/55">
                          <p>
                            Domain: {cart.spec.domain.needsRegistration
                              ? cart.spec.domain.desiredDomain
                                ? `register ${cart.spec.domain.desiredDomain}`
                                : 'registration needed'
                              : 'customer already has one'}
                          </p>
                          {cart.spec.domain.needsRegistration ? (
                            <p className="mt-1 font-black uppercase tracking-[0.12em] text-orange-300/80">
                              {cart.spec.domain.availabilityStatus === 'available'
                                ? `Available · ${formatRand(cart.spec.domain.purchaseAmount || 0)} due today`
                                : cart.spec.domain.availabilityStatus === 'unavailable'
                                  ? 'Unavailable · choose another domain'
                                  : cart.spec.domain.availabilityStatus === 'premium'
                                    ? 'Premium/unpriced · manual quote needed'
                                    : 'Live check required before checkout'}
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>

                    <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.035] p-4">
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-300/75">Feature add-ons</p>
                      <div className="mt-3 space-y-2">
                        {cart.addOns.length === 0 ? (
                          <p className="rounded-2xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-orange-100/45">
                            No add-ons selected yet.
                          </p>
                        ) : (
                          cart.addOns.map((addon) => (
                            <div
                              key={addon.id}
                              className="grid grid-cols-[1fr_auto] gap-3 rounded-2xl border border-orange-300/15 bg-orange-500/[0.045] px-3 py-2 text-xs"
                            >
                              <span>
                                <span className="block font-bold text-orange-100/80">{addon.label}</span>
                                <button
                                  type="button"
                                  onClick={() => removeAddOn(addon.id)}
                                  className="mt-1 text-[10px] font-black uppercase tracking-[0.14em] text-orange-300/70 transition-colors hover:text-orange-200"
                                >
                                  Remove add-on
                                </button>
                              </span>
                              <span className="pt-0.5 text-right font-black text-orange-200">
                                {addon.discountAmount ? (
                                  <span className="block text-[10px] text-orange-100/35 line-through">
                                    +{formatRand(addon.originalPrice || addon.price)}
                                    {cart.billingMode === 'monthly' ? '/m' : ''}
                                  </span>
                                ) : null}
                                +{formatRand(addon.price)}
                                {cart.billingMode === 'monthly' ? '/m' : ''}
                                {addon.discountAmount ? (
                                  <span className="mt-1 block text-[9px] uppercase tracking-[0.12em] text-orange-300/75">
                                    Shared backend
                                  </span>
                                ) : null}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative z-10 mt-5 rounded-[1.5rem] border border-orange-300/20 bg-black/40 p-4">
                <div className="flex items-end justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-100/42">
                      {cart?.billingMode === 'buyout' ? 'Once-off total' : 'Monthly total'}
                    </p>
                    <p className="mt-1 text-3xl font-black text-orange-100">
                      {formatRand(cart?.total || 0)}
                      {cart?.billingMode === 'monthly' ? <span className="text-sm text-orange-100/45">/m</span> : null}
                    </p>
                  </div>
                  {cart?.addOns.length ? (
                    <p className="text-right text-xs text-orange-100/45">{cart.addOns.length} add-on{cart.addOns.length === 1 ? '' : 's'}</p>
                  ) : null}
                </div>
                {message && <p className="mt-3 rounded-2xl border border-orange-300/20 bg-orange-500/10 p-3 text-xs text-orange-100">{message}</p>}
                {cart?.spec?.pricing?.addOnDiscountTotal ? (
                  <p className="mt-3 rounded-2xl border border-orange-300/20 bg-orange-500/10 p-3 text-xs text-orange-100/70">
                    Shared backend saving: {formatRand(cart.spec.pricing.addOnDiscountTotal)}
                    {cart.billingMode === 'monthly' ? '/m' : ''}
                  </p>
                ) : null}
                {domainRegistrationTotal ? (
                  <p className="mt-3 rounded-2xl border border-green-300/25 bg-green-500/10 p-3 text-xs text-green-100/80">
                    Domain registration due today: {formatRand(domainRegistrationTotal)}. Registration only proceeds after payment clears.
                  </p>
                ) : null}
                {domainCheckoutBlockReason ? (
                  <p className="mt-3 rounded-2xl border border-red-400/30 bg-red-500/10 p-3 text-xs text-red-100">
                    {domainCheckoutBlockReason}
                  </p>
                ) : null}
                <button
                  type="button"
                  onClick={handleCheckout}
                  disabled={!cart || isCheckingOut || Boolean(domainCheckoutBlockReason)}
                  className="mt-4 w-full rounded-full bg-gradient-to-r from-orange-500 to-amber-400 px-5 py-3 font-black text-black transition-colors hover:from-orange-400 hover:to-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isCheckingOut ? 'Starting checkout...' : 'Checkout'}
                </button>
                <a
                  href="/client"
                  className="mt-3 flex w-full items-center justify-center rounded-full border border-orange-300/25 bg-orange-500/[0.06] px-5 py-2 text-xs font-black uppercase tracking-[0.16em] text-orange-200 transition-colors hover:border-orange-200/70 hover:bg-orange-500/[0.12]"
                >
                  Open client area
                </a>
              </div>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
